import mapboxgl from 'mapbox-gl';
import React from 'react';
import { PageStrings, isSingleMapPage, isAtMapCollectionPage } from '@alltrails/shared/utils/pageStringHelpers';
import isEqual from 'underscore/modules/isEqual';
import { debounce } from 'debounce';
import { logPrintMapButtonClicked } from '@alltrails/analytics/amplitudeFunctions';
import { getPageTitle } from 'utils/RoutePath';
import CtaControls from '../../../../../components/map_controls/CtaControls';
import UndoRedoControls from '../../../../../components/map_controls/UndoRedoControls';
import ZoomControls from '../../../../../components/map_controls/ZoomControls';
import { addExploreItems } from '../../../../../utils/mapbox/overlays/explore_items';
import { addRemotePinMarker, removeRemotePinMarker } from '../../../../../utils/mapbox/overlays/remote_pin_markers';
import { addAtMap } from '../../../../../utils/mapbox/overlays/at_map';
import { addLifeline } from '../../../../../utils/mapbox/overlays/lifeline';
import { fitMapToBounds } from '../../../../../utils/mapbox/map_helpers';
import { getAdminCustomizationSettings, setAdminCustomizationSetting } from '../../../../../utils/mapbox/admin_customization_helpers';
import { FullscreenMapElevationPane } from './map_elevation_pane';
import { getLines, getLineCenterCoordinates } from '../../../../../utils/at_map_helpers';
import RouteControls from '../../../../../components/MapCreator/Routing/RouteControls';
import { MapMixin } from '../../mixins/map/map_mixin';
import { TrailPlannerMixin } from '../../mixins/map/trail_planner_mixin';
import { shallowCompare } from '../../../../../utils/compare';
import { removeWaypoints } from '../../../../../utils/mapbox/overlays/waypoints';
import { fireLayerEvent } from '../../../../../utils/mapbox/layers/layer_helpers';
import { overlayConfigs } from '../../../../../utils/mapbox/overlay_configs';
import Lightbox from '../../../../../components/shared/Lightbox';
import HelpButton from '../../../../../components/MapCreator/HelpButton';
import { modalPro, modalSignup } from '../../../../../utils/modal_funnel_util';
import { addEditEndpointOverlay, removeEditEndpointOverlay } from '../../../../../utils/mapbox/overlays/edit_endpoint';
import { getFormattedTrailUrls } from '../../../../../utils/trail_helpers';
import { urlReformatPhotos } from '../../../../../utils/lightbox_helpers';
import MapSelection from '../../../../../components/MapSelection';

const createReactClass = require('create-react-class');

const FullscreenSearchMap = createReactClass({
  mixins: [MapMixin, TrailPlannerMixin],
  getInitialState() {
    return {
      currentLocation: null,
      currentMapLayer: 'alltrailsOutdoorsV2',
      elevationCollapsed: false,
      enabledOverlays: [],
      expanded: false,
      mapLoaded: false,
      initialStyleLoaded: false,
      hoverCardPortal: null,
      trailPlanner: null,
      routingActive: true,
      routingMode: 'hiking',
      currentlyEditingRoute: null,
      showRoutingTips: { SMART_TIP: true, DRAW_TIP: true, NODE_TIP: true },
      selectedFilterActivity: '',
      terrainEnabled: false,
      currentLayerTerrainEnabled: true,
      adminCustomizationSettings: getAdminCustomizationSettings(),
      compassRotation: 0,
      terrainMapTipClosed: false
    };
  },
  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState, propKey => propKey !== 'resultCardFunctions');
  },
  componentDidMount() {
    if (!this.listeners) {
      this.listeners = [];
    }
    this.listeners.push(this.props.messagingChannel.subscribe('map.bounds.changed', this.viewBoundsChanged));
    this.listeners.push(this.props.messagingChannel.subscribe('waypoint.hover', this.handleWaypointHover));
    this.listeners.push(this.props.messagingChannel.subscribe('route.mouseEnter', this.handleRouteHover));
    this.listeners.push(this.props.messagingChannel.subscribe('route.mouseLeave', this.handleRouteMouseLeave));
    this.listeners.push(this.props.messagingChannel.subscribe('item.mouseenter', debounce(this.handleItemMouseEnter, 300)));
    this.listeners.push(this.props.messagingChannel.subscribe('item.mouseleave', this.handleItemMouseLeave));
    this.listeners.push(this.props.messagingChannel.subscribe('print.navigate', this.handlePrintClick));
    this.editEndpointMessagingSubscriptions();
    this.trailPlannerMessagingSubscriptions();

    this.initMap();
  },

  // Waypoint-related
  handleWaypointHover({ id, location }) {
    const layerId = 'atMap-waypoints';
    const sourceId = layerId;

    // fire mouseenter causing map to pan to include waypoint and to show popup
    const lngLat = [location.longitude, location.latitude];
    const features = this.mbMap.querySourceFeatures(sourceId, {
      filter: ['match', ['get', 'id'], id, true, false]
    });
    if (features.length > 0) {
      fireLayerEvent(this.mbMap, layerId, 'mouseenter', { lngLat, features });
    }
  },
  handleRouteHover(routeId) {
    const layerId = 'atMap-polylines';
    const sourceId = layerId;
    const features = this.mbMap.querySourceFeatures(sourceId, {
      filter: ['match', ['get', 'lineId'], routeId, true, false]
    });

    const route = this.props.exploreMap.routes.find(r => r.id === routeId);
    const lngLat = getLineCenterCoordinates(route);

    if (features.length > 0 && lngLat) {
      fireLayerEvent(this.mbMap, layerId, 'mouseenter', { lngLat, features });
    }
  },
  handleRouteMouseLeave() {
    const layerId = 'atMap-polylines';
    fireLayerEvent(this.mbMap, layerId, 'mouseleave');
  },
  // track and trail hover response
  handleItemMouseEnter(item) {
    addRemotePinMarker(this.mbMap, item, this.renderHoverCard);
  },
  handleItemMouseLeave() {
    if (isSingleMapPage(this.props.currentPage)) {
      removeRemotePinMarker(this.mbMap);
    }
  },
  handleActivityFilterChange(e) {
    this.setState({ selectedFilterActivity: e.target.value });
  },
  handleAdminCustomizationSettingsChange(key, value) {
    const adminCustomizationSettings = { ...this.state.adminCustomizationSettings, [key]: value };
    setAdminCustomizationSetting(key, value);
    this.setState({ adminCustomizationSettings });
  },
  componentDidUpdate(prevProps, prevState) {
    if (!this.state.mapLoaded || !this.state.initialStyleLoaded) {
      return;
    }
    if (this.state.expanded != prevState.expanded && window) {
      window.setTimeout(() => {
        window.dispatchEvent(new Event('resize')); // force change to be recognized by mapbox
      }, 100);
    }

    const pageChanged = this.props.currentPage !== prevProps.currentPage;
    const selectedObjectChanged = !isEqual(this.props.selectedObject, prevProps.selectedObject);
    if (pageChanged || selectedObjectChanged) {
      this.clearMap();
    }

    const areWaypointsChanged =
      prevProps.exploreMap?.waypoints?.length !== this.props.exploreMap?.waypoints?.length ||
      prevProps.exploreMap?.editWaypointId !== this.props.exploreMap?.editWaypointId;

    // if the waypoints are of unequal lengths or there is a new editing ID we need to remove waypoints layer
    // before re-rendering
    if (this.props.exploreMap && prevProps.exploreMap) {
      if (areWaypointsChanged) {
        removeWaypoints(this.mbMap, 'atMap-waypoints', this.props.messagingChannel);
      }
    }

    const singleMapPage = isSingleMapPage(this.props.currentPage);
    const mapLoadedChanged = this.state.mapLoaded !== prevState.mapLoaded;
    const resultsChanged = !singleMapPage && !isEqual(this.props.results, prevProps.results);

    if (!singleMapPage && (mapLoadedChanged || pageChanged || resultsChanged)) {
      // Draw results heatmap
      if (this.state.enabledOverlays.includes('heatmap')) {
        overlayConfigs.heatmap.add(this.mbMap, this.getOverlayConfigParams());
      }
      // Draw clustered pins
      const bounds = new mapboxgl.LngLatBounds();
      // Wrap renderHoverCard so that we can make sure the DOM content is rendered
      // within the scope of the current React app. This allows us to use shared
      // contexts then in the hover content.
      const renderHoverCardWithContext = (result) => {
        // We won't be able to actually render into the portal until the element/ref is allocated.
        const onRefReceived = element => {
          const hoverCardPortal = ReactDOM.createPortal(this.renderHoverCard(result), element);

          // Don't need to explicitly clean this up. The hover content will hide as expected when
          // the target ref/DOM element vanishes and the portal has nowhere to render to.
          this.setState({ hoverCardPortal });
        };

        return <div ref={onRefReceived}></div>;
      };
      addExploreItems(this.mbMap, 'results', this.props.results, renderHoverCardWithContext, bounds, this.props.context.currentUser?.admin);
      // Fit user recordings/maps pages to bounds,
      // because pins on these pages don't change based on the bounds of the map
      // (no Algolia use with private user maps)
      if (isAtMapCollectionPage(this.props.currentPage) && resultsChanged && (!prevProps.results || prevProps.results.length === 0)) {
        fitMapToBounds(this.mbMap, bounds);
      }
    } else if (
      (singleMapPage && (mapLoadedChanged || pageChanged || !isEqual(this.props.exploreMap, prevProps.exploreMap))) ||
      prevState.currentlyEditingRoute !== this.state.currentlyEditingRoute
    ) {
      // Draw exploreMap
      const bounds = new mapboxgl.LngLatBounds();
      // if user is using MapCreator but not editing show popups
      let polylineOpts;
      if (this.props.page === PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE) {
        polylineOpts = { routes: this.mbMap, renderPopup: this.renderRoutePopup };
      }
      const withWaypointLabels = this.props.page !== PageStrings.EXPLORE_TRAIL_MAP_PAGE;
      addAtMap({
        map: this.mbMap,
        atMap: this.props.exploreMap,
        renderWaypointPopup: this.renderWaypointPopup,
        handlePhotoClick: this.handlePhotoClick,
        bounds,
        withWaypointLabels,
        messagingChannel: this.props.messagingChannel,
        polylineOpts
      });
      if (this.props.lifeline) {
        addLifeline(this.mbMap, this.props.exploreMap, this.renderWaypointPopup, bounds, this.props.lifeline);
      }
      // Leave space for elevation graph at bottom and CTAs on the sides
      const boundsPadding = { top: 25, bottom: 180, left: 100, right: 100 };
      if (prevProps.exploreMap?.waypoints && areWaypointsChanged) {
        fitMapToBounds(this.mbMap, bounds, boundsPadding, true /* maintain zoom */);
      } else {
        fitMapToBounds(
          this.mbMap,
          bounds,
          boundsPadding,
          false /* maintain zoom */,
          this.props.page === PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE /* zoomOutByDefault */
        );
      }
    }
  },

  viewBoundsChanged({ center, bounds, zoom }) {
    this.props.handleMapBoundsChanged(center, bounds, zoom);
  },

  // Start - Print Functionality (extracted from PrintMapMixin)
  handlePrintClick() {
    logPrintMapButtonClicked({ map_location: getPageTitle(window.location.pathname) });
    this.handlePrintOrDownloadRequest('explore-trail-print');
  },
  buildPrintMapUrlParams() {
    const center = this.getMapCenter();
    const zoom = this.getMapZoom();
    const params = {
      map_center_lat: center[0],
      map_center_lon: center[1],
      map_zoom: zoom,
      map_type: this.state.currentMapLayer,
      enabled_overlays: this.state.enabledOverlays
    };
    return $.param(params);
  },
  handlePrintOrDownloadRequest(ref) {
    if (this.props.context.mobileBrowser || this.props.isMobileWidth) {
      window.location.href = window.checkLinkAndAppendLanguage('/mobile');
    } else if (this.props.context.proUser) {
      let printUrl = null;
      if (this.props.currentPage === PageStrings.EXPLORE_TRAIL_MAP_PAGE && this.props.selectedObject) {
        const { url } = getFormattedTrailUrls(this.props.selectedObject, this.props.context, null, `/print?${this.buildPrintMapUrlParams()}`);
        printUrl = window.checkLinkAndAppendLanguage(url);
      } else if (this.props.currentPage === PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE) {
        printUrl = window.checkLinkAndAppendLanguage(`/explore/recording/${this.props.selectedObject.slug}/print?${this.buildPrintMapUrlParams()}`);
      } else if (this.props.currentPage === PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE || this.props.currentPage === PageStrings.EXPLORE_LIFELINE_PAGE) {
        printUrl = window.checkLinkAndAppendLanguage(`/explore/map/${this.props.selectedObject.slug}/print?${this.buildPrintMapUrlParams()}`);
      }
      window.open(printUrl, '_self');
    } else if (this.props.context.currentUser) {
      const returnToUrl = window.location.pathname + window.location.search;
      modalPro(ref, returnToUrl);
    } else {
      const returnToUrl = window.location.pathname + window.location.search;
      modalSignup(ref, returnToUrl);
    }
  },
  // End - Print

  // Start - Edit Endpoint Functionality (extracted from EditEndpointMixin)
  editEndpointMessagingSubscriptions() {
    if (this.listeners == null) this.listeners = [];
    this.listeners.push(this.props.messagingChannel.subscribe('map.edit-endpoint-state-changed', this.handleEditEndpointStateChanged));
  },
  handleEditEndpointStateChanged(newState) {
    if (!this.state.editingEndpoint && newState.editingEndpoint) {
      if (!this.props.exploreMap) {
        return;
      }
      const tracks = this.props.exploreMap.tracks;
      if (!tracks || tracks.length === 0) {
        return;
      }
      addEditEndpointOverlay(this.mbMap, tracks[tracks.length - 1], this.handleEndpointDragEnd);
      this.setState(newState);
    } else if (this.state.editingEndpoint) {
      if (newState.canceled) {
        this.clearEditState();
      } else {
        this.props
          .saveEditEndpointChanges({
            segmentId: this.state.endSegmentId,
            cropIndex: this.state.endSegmentCropIndex,
            newEndPoint: this.state.endSegmentNewPoint
          })
          .then(this.clearEditState);
      }
    }
  },
  handleEndpointDragEnd({ endSegmentId, endSegmentCropIndex, endSegmentNewPoint }) {
    this.setState({ endSegmentId, endSegmentCropIndex, endSegmentNewPoint });
  },
  clearEditState() {
    removeEditEndpointOverlay(this.mbMap);
    this.setState({ editingEndpoint: false });
  },
  handleElevationGainChanged(elevationGain) {
    if (this.props.handleRouteInfoChanged) {
      this.props.handleRouteInfoChanged({ elevationGain });
    }
  },
  renderElevationPane() {
    if (this.state.trailPlanner || (isSingleMapPage(this.props.currentPage) && getLines(this.props.exploreMap).length > 0)) {
      return (
        <FullscreenMapElevationPane
          collapsed={this.state.elevationCollapsed}
          collapseHandleClick={this.toggleElevationCollapsed}
          displayMetric={this.props.context.displayMetric}
          getElevationData={this.state.trailPlanner}
          handleElevationGainChanged={this.handleElevationGainChanged}
          handleMouseOver={this.handleElevationPaneMouseOver}
          lifeline={this.props.lifeline}
          map={this.props.plannerMap || this.props.exploreMap}
          languageRegionCode={this.props.context.languageRegionCode}
        />
      );
    }
    return null;
  },

  handlePhotoClick(photoId, photos) {
    const lightboxStartingIndex = photos.findIndex(p => p.id === photoId);
    this.setState({ lightboxOpen: true, lightboxStartingIndex, lightboxPhotos: photos });
  },

  handleLightboxClose() {
    this.setState({ lightboxOpen: false, lightboxStartingIndex: null, lightboxPhotos: [] });
  },

  render() {
    const onMapCreatorPage = this.props.exploreMap && !this.props.exploreMap.id;
    const showControls =
      isSingleMapPage(this.props.currentPage) &&
      this.props.currentPage !== PageStrings.EXPLORE_LIFELINE_PAGE &&
      !onMapCreatorPage &&
      !this.state.trailPlanner;

    const isMobile = this.props.context.mobileBrowser || this.props.isMobileWidth;

    const isAuthenticated = !!this.props.context.currentUser;
    const isAdminOrPro = isAuthenticated && (this.props.context.currentUser.admin || this.props.context.currentUser.pro);

    const mapStyle = {
      left: isMobile || this.state.expanded ? 0 : this.props.divLeft
    };

    let mapBottomClass = '';
    let mapFullClass = 'full-height';
    if (this.props.currentPage === 'EXPLORE_COMMUNITY_CONTENT') {
      mapFullClass += ' community';
    }
    const elevationPane = this.renderElevationPane();
    if (elevationPane) {
      if (!this.state.elevationCollapsed) {
        mapBottomClass += ' big';
        mapFullClass += ' with-graph';
      }
    }

    if (typeof $ !== 'undefined') {
      const $fullscreenSearchResults = $('#fullscreen-search-results');
      if (this.state.expanded) {
        mapFullClass += ' expanded';
        $fullscreenSearchResults.css('left', -$fullscreenSearchResults.width());
      } else {
        $fullscreenSearchResults.css('left', '0px');
      }

      if (this.state.elevationCollapsed) {
        $('.mapboxgl-ctrl-bottom-right').addClass('elevation-collapsed');
      } else {
        $('.mapboxgl-ctrl-bottom-right').removeClass('elevation-collapsed');
      }
    }

    const permissions = this.getPermissions();

    return (
      <div id="fullscreen-search-map" className={mapFullClass} style={mapStyle}>
        {isMobile ? null : <div id="map-expand-handle" onClick={this.toggleExpanded} />}
        {this.renderTerrainTip()}
        <div className="map-routing-controls">
          {this.state.trailPlanner && (
            <RouteControls
              context={this.props.context}
              routingActive={this.state.routingActive}
              routingMode={this.state.routingMode}
              onSelectRoutingMode={this.setRoutingMode}
              toggleRouting={this.toggleRouting}
              showRoutingTips={this.state.showRoutingTips}
              closeRoutingTip={this.toggleRoutingTip}
              nodeSelected={this.state.selectedRouteNode}
            />
          )}
        </div>
        {showControls && (
          <div id="map-controls">
            <CtaControls handlePrintClick={this.handlePrintClick} />
          </div>
        )}
        <div id={this.props.mapDivId} className="full-height" />
        {this.props.page === PageStrings.EXPLORE_USERS_MAPS_MAP_PAGE && this.state.trailPlanner && (
          <div className="map-creator-help-button-container">
            <HelpButton />
          </div>
        )}
        <div className={`map-orient-controls${mapBottomClass}`}>
          {this.state.trailPlanner && <UndoRedoControls handleUndoClick={this.undoClick} handleRedoClick={this.redoClick} />}
          <ZoomControls
            languageRegionCode={this.props.context.languageRegionCode}
            isAuthenticated={isAuthenticated}
            isPro={isAdminOrPro}
            terrainActive={this.state.terrainActive}
            currentLayerTerrainEnabled={this.state.currentLayerTerrainEnabled}
            compassRotation={this.state.compassRotation}
            handleCompassClick={this.handleCompassClick}
            handleDisable3D={this.disable3D}
            handleEnable3D={this.enable3D}
            handleZoomInClick={this.zoomInClicked}
            handleZoomOutClick={this.zoomOutClicked}
            handleCurrLocClick={this.compassClick}
          />
        </div>
        <div className={`map-overlays-layers-controls${mapBottomClass}`}>
          <MapSelection
            context={this.props.context}
            enabledOverlays={this.state.enabledOverlays}
            onActivityFilterChange={this.handleActivityFilterChange}
            selectedFilterActivity={this.state.selectedFilterActivity}
            adminCustomizationSettings={this.state.adminCustomizationSettings}
            handleAdminCustomizationSettingsChange={this.handleAdminCustomizationSettingsChange}
            currentMapLayer={this.state.currentMapLayer}
            onCardClick={this.handleCardClick}
            onHeatmapClick={this.handleHeatmapOverlayClick}
            permissions={permissions}
            terrainActive={this.state.terrainActive}
            handleResetClick={this.disableAllOverlays}
            adminLayout={this.props.context && this.props.context.adminUser}
          />
        </div>
        {elevationPane}
        {this.state.lightboxOpen && (
          <Lightbox
            images={urlReformatPhotos(this.state.lightboxPhotos)}
            handleCloseRequest={this.handleLightboxClose}
            startingIndex={this.state.lightboxStartingIndex}
          />
        )}
        {/* This is the magic that allows the hover cards to share global app state.
        The content is rendered in the card DOM, but derived from here in the React app tree */}
        {this.state.hoverCardPortal}
      </div>
    );
  }
});

export { FullscreenSearchMap };
